import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useGetImportEmployeeById } from '@nxsys/hooks/api/import';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { ColumnsType } from 'antd/es/table';
import { AppState } from '@nxsys/store';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IMPORT_COLUMNS_WIDTH } from '../constants';
import {
  getCISImportXMLColumns,
  getFPSImportXMLColumns,
} from '../listing/columns';
import UploadedFileInformation from './UploadedFileInformation';

type ViewImportedEmployeeFileProps = {
  isOpen: boolean;
  toggle: () => void;
  id?: string;
  fileType: string;
};

export enum IMPORT_TYPE {
  XML = 'XML',
  EXCEL = 'EXCEL',
}

const ViewImportedEmployeeFile: React.FC<ViewImportedEmployeeFileProps> = ({
  isOpen,
  toggle,
  id,
  fileType,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );

  const {
    value,
    run: getImportEmployeeById,
    loading,
  } = useGetImportEmployeeById();
  const {
    data = [],
    fileName,
    fileSize,
    totalRecords = 0,
    headersImportFile = [],
    companyName,
    xmlType,
    employees,
  } = value?.data || {};

  useEffect(() => {
    if (id && isOpen) getImportEmployeeById(id);
  }, [id, isOpen]);

  const excelColumns: ColumnsType<any> = useMemo(() => {
    return (
      headersImportFile?.map((columnHeader, index) => ({
        key: `${columnHeader}_${index}`,
        title: <div className="truncate">{columnHeader}</div>,
        dataIndex: index,
        width:
          IMPORT_COLUMNS_WIDTH?.[
            columnHeader as keyof typeof IMPORT_COLUMNS_WIDTH
          ] ?? 200,
      })) ?? []
    );
  }, [headersImportFile, messages]);

  const isCISData = xmlType === 'CIS300';

  const fpsColumns = useMemo(
    (): ColumnsType<IImportEmployeeXmlType> =>
      getFPSImportXMLColumns({ messages, payrollCountry }),
    [messages, payrollCountry],
  );

  const cisColumns = useMemo(
    (): ColumnsType<IImportEmployeeXmlType> =>
      getCISImportXMLColumns({ messages }),
    [messages],
  );

  const employeeXmlColumns = useMemo(() => {
    return isCISData ? cisColumns : fpsColumns;
  }, [isCISData, cisColumns, fpsColumns]);

  const scrollWidth = isCISData ? 2800 : 10000;

  return (
    <AppModalContainer
      isOpen={isOpen}
      toggle={toggle}
      title={messages['bulkUpdate.modal.fileInformation']}
      width="90%"
    >
      <UploadedFileInformation
        fileName={fileName}
        fileSize={fileSize}
        totalRecords={totalRecords}
      />
      {fileType === IMPORT_TYPE.EXCEL ? (
        <AppTableContainer
          data={data && data.length > 1 ? data?.slice(1) : []}
          loading={loading}
          page={0}
          total={totalRecords}
          pageSize={totalRecords}
          columns={excelColumns}
          scroll={{ x: 10000, y: 500 }}
          {...(totalRecords > 1000 ? { virtual: true } : {})}
        />
      ) : (
        <>
          <div className="flex mb-2">
            <div>{isBureau ? messages['common.bureau.companyName'] : messages['common.companyName']}:</div>
            <div className="ml-2">{companyName || '-'}</div>
          </div>
          <AppTableContainer
            data={employees ?? []}
            loading={loading}
            page={0}
            total={employees?.length ?? 0}
            pageSize={employees?.length ?? 0}
            columns={employeeXmlColumns}
            scroll={{ x: scrollWidth, y: 500 }}
            {...((employees?.length ?? 0 > 1000) ? { virtual: true } : {})}
          />
        </>
      )}
    </AppModalContainer>
  );
};

export default ViewImportedEmployeeFile;
